// Versioned TTL-based localStorage caching utilities
// Provides safe cache operations with expiration and versioning

interface CachedItem<T> {
  data: T;
  timestamp: number;
  ttl: number;
  version: number;
}

interface CacheStats {
  totalEntries: number;
  freshEntries: number;
  expiredEntries: number;
  sizes: { [key: string]: number };
}

const CACHE_VERSION = 1;
const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes

class Cache {
  private getStorageKey(key: string): string {
    return `cache_v${CACHE_VERSION}_${key}`;
  }

  // Get item from cache if fresh
  get<T>(key: string): T | null {
    try {
      const storageKey = this.getStorageKey(key);
      const cached = localStorage.getItem(storageKey);
      
      if (!cached) {
        if (process.env.NODE_ENV === 'development') {
          console.debug(`[Cache] Miss: ${key}`);
        }
        return null;
      }

      const item: CachedItem<T> = JSON.parse(cached);
      const now = Date.now();
      
      // Check version mismatch
      if (item.version !== CACHE_VERSION) {
        localStorage.removeItem(storageKey);
        if (process.env.NODE_ENV === 'development') {
          console.debug(`[Cache] Version mismatch: ${key}`);
        }
        return null;
      }

      // Check expiration
      if (now - item.timestamp > item.ttl) {
        localStorage.removeItem(storageKey);
        if (process.env.NODE_ENV === 'development') {
          console.debug(`[Cache] Expired: ${key}`);
        }
        return null;
      }

      if (process.env.NODE_ENV === 'development') {
        const ageMs = now - item.timestamp;
        console.debug(`[Cache] Hit: ${key} (age: ${Math.round(ageMs / 1000)}s)`);
      }
      
      return item.data;
    } catch (error) {
      console.error(`[Cache] Error reading ${key}:`, error);
      this.delete(key);
      return null;
    }
  }

  // Set item in cache with TTL
  set<T>(key: string, data: T, ttl: number = DEFAULT_TTL): void {
    try {
      const item: CachedItem<T> = {
        data,
        timestamp: Date.now(),
        ttl,
        version: CACHE_VERSION
      };
      
      const storageKey = this.getStorageKey(key);
      localStorage.setItem(storageKey, JSON.stringify(item));
      
      if (process.env.NODE_ENV === 'development') {
        const sizeKB = Math.round(JSON.stringify(item).length / 1024);
        console.debug(`[Cache] Set: ${key} (${sizeKB}KB, TTL: ${Math.round(ttl / 1000)}s)`);
      }
    } catch (error) {
      console.error(`[Cache] Error setting ${key}:`, error);
    }
  }

  // Delete specific cache entry
  delete(key: string): void {
    try {
      const storageKey = this.getStorageKey(key);
      localStorage.removeItem(storageKey);
      
      if (process.env.NODE_ENV === 'development') {
        console.debug(`[Cache] Deleted: ${key}`);
      }
    } catch (error) {
      console.error(`[Cache] Error deleting ${key}:`, error);
    }
  }

  // Clear all cache entries matching a prefix
  clearPrefix(prefix: string): void {
    try {
      const keysToDelete: string[] = [];
      const prefixKey = this.getStorageKey(prefix);
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefixKey)) {
          keysToDelete.push(key);
        }
      }
      
      keysToDelete.forEach(key => localStorage.removeItem(key));
      
      if (process.env.NODE_ENV === 'development') {
        console.debug(`[Cache] Cleared ${keysToDelete.length} entries with prefix: ${prefix}`);
      }
    } catch (error) {
      console.error(`[Cache] Error clearing prefix ${prefix}:`, error);
    }
  }

  // Get cache statistics for debugging
  getStats(): CacheStats {
    const stats: CacheStats = {
      totalEntries: 0,
      freshEntries: 0,
      expiredEntries: 0,
      sizes: {}
    };

    try {
      const now = Date.now();
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key || !key.startsWith(`cache_v${CACHE_VERSION}_`)) continue;
        
        stats.totalEntries++;
        
        try {
          const value = localStorage.getItem(key);
          if (!value) continue;
          
          const item: CachedItem<any> = JSON.parse(value);
          const age = now - item.timestamp;
          
          if (age > item.ttl) {
            stats.expiredEntries++;
          } else {
            stats.freshEntries++;
          }
          
          stats.sizes[key] = Math.round(value.length / 1024); // KB
        } catch (parseError) {
          stats.expiredEntries++;
        }
      }
    } catch (error) {
      console.error('[Cache] Error computing stats:', error);
    }

    return stats;
  }

  // Clean up expired entries
  cleanup(): void {
    try {
      const keysToDelete: string[] = [];
      const now = Date.now();
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key || !key.startsWith(`cache_v${CACHE_VERSION}_`)) continue;
        
        try {
          const value = localStorage.getItem(key);
          if (!value) {
            keysToDelete.push(key);
            continue;
          }
          
          const item: CachedItem<any> = JSON.parse(value);
          if (now - item.timestamp > item.ttl) {
            keysToDelete.push(key);
          }
        } catch (parseError) {
          keysToDelete.push(key);
        }
      }
      
      keysToDelete.forEach(key => localStorage.removeItem(key));
      
      if (process.env.NODE_ENV === 'development' && keysToDelete.length > 0) {
        console.debug(`[Cache] Cleaned up ${keysToDelete.length} expired entries`);
      }
    } catch (error) {
      console.error('[Cache] Error during cleanup:', error);
    }
  }
}

// Global cache instance
export const cache = new Cache();

// Automatic cleanup every 10 minutes
setInterval(() => {
  cache.cleanup();
}, 10 * 60 * 1000);

// Safe clear function for specific prefixes
export function safeClear(prefix: string): void {
  cache.clearPrefix(prefix);
}

// Export cache utilities for specific use cases
export function getCachedActions(userId: string): any[] | null {
  return cache.get(`actions:${userId}`);
}

export function setCachedActions(userId: string, actions: any[], ttl: number = 3 * 60 * 1000): void {
  cache.set(`actions:${userId}`, actions, ttl);
}

export function getCachedPortalActions(clientId: string, userId: string): any[] | null {
  return cache.get(`portal:actions:${clientId}:${userId}`);
}

export function setCachedPortalActions(clientId: string, userId: string, actions: any[], ttl: number = 3 * 60 * 1000): void {
  cache.set(`portal:actions:${clientId}:${userId}`, actions, ttl);
}